import { type FC, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { ConfirmDialog } from "@/shared/components";
import { useAbility } from "@/partner/shared/lib";
import { useTerminateProductPurchaseMutation } from "@/partner/entities/purchases";

interface TerminatePurchaseButtonProps {
  id: string;
  expiredAt: string;
}

/**
 * Остановка покупки партнёром (референс админской TerminatePurchaseButton):
 * доступ истекает сейчас, автопродление подписки выключается. Действие
 * необратимое, поэтому подтверждение с задержкой (delay), как у соседних
 * необратимых действий кабинета. У истёкшей покупки останавливать нечего,
 * кнопки нет.
 */
export const TerminatePurchaseButton: FC<TerminatePurchaseButtonProps> = ({
  id,
  expiredAt,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const ability = useAbility();
  const [open, setOpen] = useState(false);

  const [terminate, { loading, error }] = useTerminateProductPurchaseMutation({
    variables: { id },
    refetchQueries: ["PurchasesList"],
    onCompleted: () => {
      enqueueSnackbar(t("purchases.actions.terminateSuccessSnackbar"), {
        variant: "success",
      });
      setOpen(false);
    },
    onError: () => {
      // ошибка уже отображается через ErrorMessage внутри ConfirmDialog
    },
  });

  if (!ability.can("update", "ProductPurchase")) return null;
  if (new Date(expiredAt).getTime() <= Date.now()) return null;

  return (
    <>
      <Tooltip title={t("purchases.actions.terminateButton")}>
        <span>
          <IconButton
            size="small"
            disabled={loading}
            onClick={(event) => {
              event.stopPropagation();
              setOpen(true);
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <ConfirmDialog
        open={open}
        loading={loading}
        error={error}
        delay={5}
        onCancel={() => setOpen(false)}
        onConfirm={() => {
          void terminate();
        }}
        confirmTitle={t("purchases.actions.terminateConfirmTitle")}
        confirmText={t("purchases.actions.terminateConfirmText")}
      />
    </>
  );
};
